import { useCallback, useRef, useState } from 'react';

/**
 * Per-pane width persistence for resizable list panes (the master column in a
 * list→preview view). Each pane gets its own localStorage key so widths are
 * independent across views. Mirrors the direct-localStorage pattern in
 * shell/storage.ts — no store, no context, just a namespaced key.
 *
 * The shell's left nav rail does NOT use this; its width lives in the workspace
 * PersistedState (shell/storage.ts) alongside theme/layout. This hook is for the
 * secondary, per-view list panes (Conversations, Skills, …).
 */
const KEY_PREFIX = 'shiftagent-pane-w:';

function loadWidth(key: string, fallback: number): number {
  try {
    const raw = localStorage.getItem(KEY_PREFIX + key);
    if (raw == null) return fallback;
    const n = Number(raw);
    return Number.isFinite(n) ? n : fallback;
  } catch {
    return fallback;
  }
}

function saveWidth(key: string, w: number): void {
  try {
    localStorage.setItem(KEY_PREFIX + key, String(Math.round(w)));
  } catch {
    // Storage unavailable / quota — non-fatal; the width just won't persist.
  }
}

export interface ResizableWidth {
  /** Current (live) width in px to apply to the pane. */
  width: number;
  /** True while a drag is in progress — suppress width transitions if any. */
  dragging: boolean;
  min: number;
  max: number;
  /** Live update during drag (also persists — cheap, batched). */
  onResize: (w: number) => void;
  /** Settle at the end of a drag. */
  onResizeEnd: (w: number) => void;
}

export function useResizableWidth(
  key: string,
  defaultWidth: number,
  min: number,
  max: number,
): ResizableWidth {
  const [width, setWidth] = useState(() => loadWidth(key, defaultWidth));
  const [dragging, setDragging] = useState(false);
  const latest = useRef(width);

  const onResize = useCallback(
    (w: number) => {
      if (!dragging) setDragging(true);
      latest.current = w;
      setWidth(w);
    },
    [dragging],
  );

  const onResizeEnd = useCallback(
    (w: number) => {
      setDragging(false);
      latest.current = w;
      setWidth(w);
      saveWidth(key, w);
    },
    [key],
  );

  return { width, dragging, min, max, onResize, onResizeEnd };
}
